import { css } from '@emotion/react'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { memo, useCallback, useMemo, useRef, useState } from 'react'
import {
    SignupFields,
    SignupPostFields,
    SignupResponse,
    ValidateSignupFields,
    ValidateSignupResponse,
    signupPost,
    validateSignupPost,
} from '../../../mfcLogic/auth'
import { setAppState } from '../../../state/state.app'
import { onInputBlur, onInputFocus } from '../../../state/state.layout'
import i18n from '../../../utilities/i18n'
import { routerPush } from '../../../utilities/router'
import { safeObj } from '../../../utilities/safe'
import Button from '../../Buttons/Button'
import {
    AppStateType,
    useAppState,
    useAppStateSelector,
} from '../../Hooks/useAppState'
import Eye from '../../Icons/Eye'
import EyeOff from '../../Icons/EyeOff'
import { StyledWarningText, muiInputStyles } from '../../common/Forms'
import { getThemeSpacing } from '../../styles/appTheme'
import APP_BREAKPOINTS, { minWindowWidth } from '../../styles/breakpoints'
import Link from '../Link'
import Login from './Login'
import SignupSuccess from './SignupSuccess'
import SignupVerified from './SignupVerified'
import ThirdPartyProviders from './ThirdPartyProviders'
import { eyeIconStyle, validationWarningStyles } from './shared'

const rootStyles = css({
    padding: getThemeSpacing(1),
    maxWidth: 500,
    [minWindowWidth(APP_BREAKPOINTS.sm)]: {
        padding: getThemeSpacing(2),
    },
})
const formStyles = css({
    width: '100%',
    display: 'flex',
    flexFlow: 'row wrap',
    '& .MuiTextField-root': {
        width: '100%',
        marginTop: 0,
        marginBottom: getThemeSpacing(1),
        '&:first-of-type': {
            marginTop: 0,
        },
    },
})
const fieldStyles = css({
    '& p > span': {
        display: 'block',
    },
    ...muiInputStyles,
})
const consentStyles = css({
    marginBottom: getThemeSpacing(3),
    marginLeft: getThemeSpacing(-1),
    marginTop: getThemeSpacing(2),
})

const buttonSubmitStyles = css({
    marginBottom: getThemeSpacing(2),
})

type InputType = 'username' | 'password' | 'email'

type StateKey =
    | 'consent'
    | 'email'
    | 'username'
    | 'new_password'
    | 'skipEmailValidation'

const SignUp = memo(() => {
    const authenticated = useAppStateSelector(AppStateType.APP, 'authenticated')
    const authState = useAppState(AppStateType.AUTHENTICATION)
    const {
        errors: { signup: errors = {} } = {},
        success,
        verified,
    } = authState
    const [consent, setConsent] = useState(false)
    const [email, setEmail] = useState('')
    const [username, setUsername] = useState('')
    const [new_password, setNewPassword] = useState('')
    const [skipEmailValidation, setSkipEmailValidation] = useState(false)
    const [masked, setMasked] = useState(true)
    const onToggleMasked = useCallback(() => setMasked(!masked), [masked])

    const previous = useRef({
        consent,
        email,
        username,
        password: new_password,
        new_password,
        skipEmailValidation,
    })

    const state = {
        consent,
        email,
        username,
        password: new_password,
        new_password,
        skipEmailValidation,
    }

    const setState = (name: StateKey, value: any) => {
        switch (name) {
            case 'consent':
                return setConsent(value)
            case 'email':
                return setEmail(value)
            case 'username':
                return setUsername(value)
            case 'new_password':
                return setNewPassword(value)
        }
    }

    const isValid = (type?: InputType) => {
        if (!type) {
            const {
                username: unErrors = [],
                password: psErrors = [],
                email: eErrors = [],
            } = errors
            return !!(
                (skipEmailValidation || !eErrors.length) &&
                !unErrors.length &&
                !psErrors.length &&
                new_password &&
                username &&
                consent &&
                email
            )
        }
        const { [type]: messages = [] } = errors
        if (!messages.length) return true
        if (type === 'email' && (skipEmailValidation || !email)) return true
        if (type === 'password' && !new_password) {
            return true
        }
        if (type === 'username' && !username) return true
        return false
    }

    const onSignupResponse = (
        payload: SignupResponse | ValidateSignupResponse,
    ) => {
        const { errmsg, result } = safeObj(payload)
        const success = result === 'verification_email_sent'

        setAppState(AppStateType.AUTHENTICATION, {
            errors: { signup: errmsg },
            success,
        })

        // Marc:
        // we need to remove "global.__APP_SIGNUP_TRACKER"
        // just doing this to fix a bug quickly for the release today
        // 12/20/2022
        if (success) {
            global.__APP_SIGNUP_TRACKER = true
        }
    }

    const validateSignup = (
        name: SignupFields,
        fields: ValidateSignupFields,
    ) => {
        validateSignupPost(name, fields).then((response) => {
            onSignupResponse(response)
        })
    }

    const signup = (fields: SignupPostFields) => {
        if (!fields) return
        signupPost(fields).then((signupResponse) => {
            if (signupResponse.err > 0) {
                // warn the user
                onSignupResponse(signupResponse)
            }
            signupSetupInterval(signupResponse)
        })
    }

    const signupSetupInterval = (
        response: SignupResponse,
        interval: ReturnType<typeof setInterval> = null,
    ) => {
        onSignupResponse(response)
        const { result } = response
        if (result !== 'verification_email_sent') {
            return
        }
        interval = setInterval(() => {
            if (!authenticated) return
            clearInterval(interval)
            setAppState(AppStateType.AUTHENTICATION, { verified: true })
            routerPush('/')
        }, 1000)
    }

    const onBlur =
        (name: Exclude<StateKey, 'consent' | 'skipEmailValidation'>) => () => {
            onInputBlur()
            const { [name]: current } = state
            const { [name]: last } = previous.current
            if (last === current) return
            validateSignup(name, state)
            previous.current = {
                ...previous.current,
                [name]: current,
            }
        }

    const onChange = (name: StateKey) => (event) => {
        const { target } = event
        if (name === 'consent') {
            const { checked } = target
            setConsent(checked)
            return
        }
        const { value } = target
        if (name === 'email') {
            setSkipEmailValidation(true)
        }
        setState(name, value)
    }

    const onSignup = (event = null) => {
        event?.preventDefault()
        if (!isValid()) return
        signup(state)
        setSkipEmailValidation(false)
    }

    const onEnter = (event) => {
        const { key } = event
        if (key !== 'Enter') return
        event.preventDefault()
        onSignup()
    }

    const validate = (type: InputType) => {
        if (isValid(type)) {
            return null
        }
        const { [type]: messages = [] } = errors
        return messages.map((message, index) => (
            <span css={validationWarningStyles} key={index}>
                {'* ' + message}
            </span>
        ))
    }

    const errMessages = useMemo(() => {
        return errors.general || []
    }, [errors])

    if (authenticated && !success && !verified) {
        return <Login />
    }

    if (verified) {
        return <SignupVerified />
    }

    if (success) {
        return <SignupSuccess email={email} />
    }

    return (
        <div css={rootStyles}>
            <form
                data-testid="SignUpModal__Form"
                css={formStyles}
                onKeyUp={onEnter}
                noValidate
            >
                <TextField
                    label={i18n.t('signup.username')}
                    data-testid="SignUpModal__UsernameInput"
                    helperText={validate('username')}
                    onChange={onChange('username')}
                    error={!isValid('username')}
                    onBlur={onBlur('username')}
                    onFocus={onInputFocus}
                    autoComplete="username"
                    css={fieldStyles}
                    variant="outlined"
                    required
                />
                <TextField
                    label={i18n.t('signup.email')}
                    data-testid="SignUpModal__EmailInput"
                    helperText={validate('email')}
                    onChange={onChange('email')}
                    error={!isValid('email')}
                    onBlur={onBlur('email')}
                    onFocus={onInputFocus}
                    css={fieldStyles}
                    variant="outlined"
                    type="email"
                    required
                />
                <div css={{ position: 'relative', width: '100%' }}>
                    <TextField
                        label={i18n.t('signup.password')}
                        data-testid="SignUpModal__PasswordInput"
                        helperText={validate('password')}
                        onChange={onChange('new_password')}
                        error={!isValid('password')}
                        onBlur={onBlur('new_password')}
                        onFocus={onInputFocus}
                        autoComplete="new-password"
                        css={fieldStyles}
                        variant="outlined"
                        type={masked ? 'password' : 'text'}
                        required
                    />
                    {masked ? (
                        <Eye css={eyeIconStyle} onClick={onToggleMasked} />
                    ) : (
                        <EyeOff css={eyeIconStyle} onClick={onToggleMasked} />
                    )}
                </div>
                <FormControlLabel
                    css={consentStyles}
                    control={
                        <Checkbox
                            data-testid="SignUpModal__Checkbox"
                            onChange={onChange('consent')}
                            checked={consent}
                            color="primary"
                            value="given"
                        />
                    }
                    label={
                        // FormControlLabel will always wrap this in a typography component
                        // so converting this isn't useful til we get rid of it
                        <Typography variant="caption">
                            {i18n.t('signup.consent')}
                            <Link
                                href="https://wiki.myfreecams.com/wiki/Terms_and_Conditions"
                                inline
                                mfc
                            >
                                {i18n.t('signup.terms')}
                            </Link>
                            {' and '}
                            <Link
                                href="https://wiki.myfreecams.com/wiki/Privacy_Policy"
                                inline
                                mfc
                            >
                                {i18n.t('signup.privacyPolicy')}
                            </Link>
                            .
                        </Typography>
                    }
                />
                {errMessages.length > 0 && (
                    <div
                        data-testid="SignUpDrawer__ErrorMessages"
                        css={{
                            paddingBottom: 24,
                            fontWeight: 700,
                            marginLeft: 24,
                            marginRight: 24,
                        }}
                    >
                        <StyledWarningText>
                            {errMessages.map((error, idx) => (
                                <li key={idx}>{error}</li>
                            ))}
                        </StyledWarningText>
                    </div>
                )}
                <Button
                    data-testid="SignUpModal__CreateButton"
                    css={buttonSubmitStyles}
                    disabled={!isValid()}
                    onClick={onSignup}
                    size="large"
                >
                    {i18n.t('signup.createAccount')}
                </Button>
            </form>
            <ThirdPartyProviders />
        </div>
    )
})

export default SignUp
